import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

interface IndustryContent {
  metaTitle: string
  metaDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  primaryCta: string
  secondaryCta: string
  badges: string[]
  pillarsEyebrow: string
  pillarsTitle: string
  pillarsBody: string
  pillars: Pillar[]
  howEyebrow: string
  howTitle: string
  steps: Step[]
  relatedEyebrow: string
  relatedTitle: string
  related: RelatedItem[]
}

const content: Record<'en' | 'tr', IndustryContent> = {
  en: {
    metaTitle: 'Identity & access for telecommunications',
    metaDescription:
      'Brokered NOC and OSS access, subscriber CIAM at scale, sovereign deployment, and partner federation for telcos and MVNOs.',
    heroEyebrow: 'Industries · Telecommunications',
    heroTitle: ['From the NOC', 'to the subscriber.'],
    heroBody:
      'Brokered access to every network element, CIAM for tens of millions of subscribers, and standards-based federation with MVNOs, OEMs, and roaming partners, on one platform.',
    primaryCta: 'Start free trial →',
    secondaryCta: 'Talk with our experts',
    badges: [
      'Sovereign deployment',
      'High-throughput CIAM',
      'Trusted by Saudi Telecom Company',
    ],
    pillarsEyebrow: 'What you get',
    pillarsTitle: 'What telcos get with Monofor.',
    pillarsBody:
      'Telecom identity has to span workforce, subscribers, and partners: at scale, under regulation, with audit posture. Monofor is built for that span.',
    pillars: [
      {
        title: 'Network operations, with brakes',
        description:
          'NOC engineers, field technicians, and OSS operators get brokered, recorded, time-bound access to network elements, not flat administrative reach.',
        bullets: [
          'Brokered access to routers, switches, and OSS / BSS',
          'Command-level scoping for SSH access',
          'Just-in-time elevation tied to change windows',
          'Vendor / integrator access with hard expiry',
        ],
      },
      {
        title: 'Subscriber identity at scale',
        description:
          'Tens of millions of subscriber accounts. CIAM in the same platform that runs workforce identity, so policy, audit, and residency align.',
        bullets: [
          'CIAM and workforce identity on one platform',
          'Per-region data residency and routing',
          'High-throughput authentication endpoints',
          'API-driven enrollment for OEM and MVNO partners',
        ],
      },
      {
        title: 'Regulated, sovereign, observable',
        description:
          'Telecom regulators care about identity. Monofor is engineered for sovereign deployment, long-retention audit, and lawful-intercept-friendly logging.',
        bullets: [
          'Sovereign self-hosted deployment',
          'Long-retention audit trail',
          'GDPR / KVKK / national-regulator alignment',
          'Per-region tenant isolation',
        ],
      },
      {
        title: 'Partner and roaming-friendly federation',
        description:
          'Federate with MVNOs, OEMs, and roaming partners through standards-based protocols, not proprietary hand-offs.',
        bullets: [
          'SAML 2 / OIDC federation with partners',
          'SCIM-driven partner identity provisioning',
          'Per-partner policy and audit isolation',
          'Just-in-time partner deprovisioning',
        ],
      },
    ],
    howEyebrow: 'How it works',
    howTitle: 'Broker. Scale. Federate.',
    steps: [
      {
        title: 'Broker the network',
        description:
          'Route every NOC, OSS, and field-engineer session through Monopam. Vault credentials. Record. Time-bound.',
      },
      {
        title: 'Scale subscriber identity',
        description:
          'Run CIAM on the same platform as workforce identity. Per-region residency. High-throughput endpoints. API-driven partner enrollment.',
      },
      {
        title: 'Federate with partners, on standards',
        description:
          'Bring MVNOs, OEMs, and roaming partners in through SAML / OIDC / SCIM, with per-partner policy and audit.',
      },
    ],
    relatedEyebrow: 'Keep exploring',
    relatedTitle: 'Related Monofor capabilities.',
    related: [
      {
        label: 'Monopam Gateway',
        description: 'Brokered access for network elements and OSS / BSS.',
        href: '/pam/gateway',
      },
      {
        label: 'Single Sign-On',
        description: 'Federation with partners, MVNOs, and OEMs.',
        href: '/sign/sso',
      },
      {
        label: 'External & partner access',
        description: 'Use case: partner federation without flat-network exposure.',
        href: '/solutions/external-partners',
      },
    ],
  },
  tr: {
    metaTitle: 'Telekomünikasyon için kimlik ve erişim',
    metaDescription:
      'Telekom operatörleri ve MVNO’lar için aracılı NOC ve OSS erişimi, ölçekli abone CIAM’i, egemen kurulum ve iş ortağı federasyonu.',
    heroEyebrow: 'Sektörler · Telekomünikasyon',
    heroTitle: ['NOC’tan', 'aboneye kadar.'],
    heroBody:
      'Her ağ elemanına aracılı erişim, on milyonlarca abone için CIAM ve MVNO’lar, OEM’ler ve dolaşım ortaklarıyla standart tabanlı federasyon, tek platformda.',
    primaryCta: 'Ücretsiz deneme başlat →',
    secondaryCta: 'Uzmanlarımızla görüşün',
    badges: [
      'Egemen kurulum',
      'Yüksek verimli CIAM',
      'Saudi Telecom Company’nin tercihi',
    ],
    pillarsEyebrow: 'Neler elde edersiniz',
    pillarsTitle: 'Telekom operatörlerinin Monofor ile elde ettikleri.',
    pillarsBody:
      'Telekom kimliği; çalışanları, aboneleri ve iş ortaklarını ölçekte, regülasyon altında ve denetim duruşuyla kapsamak zorundadır. Monofor bu kapsam için tasarlandı.',
    pillars: [
      {
        title: 'Ağ operasyonları, frenleriyle birlikte',
        description:
          'NOC mühendisleri, saha teknisyenleri ve OSS operatörleri; sınırsız yönetimsel erişim yerine ağ elemanlarına aracılı, kayıtlı ve süreli erişim alır.',
        bullets: [
          'Yönlendiriciler, anahtarlar ve OSS / BSS’e aracılı erişim',
          'SSH erişiminde komut düzeyinde kapsam belirleme',
          'Değişiklik pencerelerine bağlı tam zamanında yetki yükseltme',
          'Kesin süre sonlu tedarikçi / entegratör erişimi',
        ],
      },
      {
        title: 'Ölçekte abone kimliği',
        description:
          'On milyonlarca abone hesabı. CIAM, çalışan kimliğini çalıştıran platformun içinde; böylece politika, denetim ve veri yerleşimi hizalanır.',
        bullets: [
          'Tek platformda CIAM ve çalışan kimliği',
          'Bölge başına veri yerleşimi ve yönlendirme',
          'Yüksek verimli kimlik doğrulama uç noktaları',
          'OEM ve MVNO ortakları için API tabanlı kayıt',
        ],
      },
      {
        title: 'Regüle, egemen, gözlemlenebilir',
        description:
          'Telekom regülatörleri kimliği önemser. Monofor; egemen kurulum, uzun saklama süreli denetim ve yasal dinlemeye uygun loglama için tasarlandı.',
        bullets: [
          'Egemen, kendi altyapınızda kurulum',
          'Uzun saklama süreli denetim izi',
          'GDPR / KVKK / ulusal regülatör uyumu',
          'Bölge başına kiracı (tenant) izolasyonu',
        ],
      },
      {
        title: 'İş ortağı ve dolaşım dostu federasyon',
        description:
          'MVNO’lar, OEM’ler ve dolaşım ortaklarıyla özel geçici çözümlerle değil, standart tabanlı protokollerle federe olun.',
        bullets: [
          'İş ortaklarıyla SAML 2 / OIDC federasyonu',
          'SCIM tabanlı iş ortağı kimlik sağlama',
          'İş ortağı başına politika ve denetim izolasyonu',
          'Tam zamanında iş ortağı yetki kaldırma',
        ],
      },
    ],
    howEyebrow: 'Nasıl çalışır',
    howTitle: 'Aracılayın. Ölçekleyin. Federe edin.',
    steps: [
      {
        title: 'Ağı aracılayın',
        description:
          'Her NOC, OSS ve saha mühendisi oturumunu Monopam üzerinden yönlendirin. Kimlik bilgilerini kasada tutun. Kaydedin. Süreyle sınırlayın.',
      },
      {
        title: 'Abone kimliğini ölçekleyin',
        description:
          'CIAM’i çalışan kimliğiyle aynı platformda çalıştırın. Bölge başına veri yerleşimi. Yüksek verimli uç noktalar. API tabanlı iş ortağı kaydı.',
      },
      {
        title: 'İş ortaklarıyla standartlar üzerinden federe olun',
        description:
          'MVNO’ları, OEM’leri ve dolaşım ortaklarını SAML / OIDC / SCIM ile dahil edin, iş ortağı başına politika ve denetimle.',
      },
    ],
    relatedEyebrow: 'Keşfetmeye devam edin',
    relatedTitle: 'İlgili Monofor yetenekleri.',
    related: [
      {
        label: 'Monopam Gateway',
        description: 'Ağ elemanları ve OSS / BSS için aracılı erişim.',
        href: '/pam/gateway',
      },
      {
        label: 'Tek Oturum Açma (SSO)',
        description: 'İş ortakları, MVNO’lar ve OEM’lerle federasyon.',
        href: '/sign/sso',
      },
      {
        label: 'Dış ve iş ortağı erişimi',
        description: 'Kullanım senaryosu: düz ağ maruziyeti olmadan iş ortağı federasyonu.',
        href: '/solutions/external-partners',
      },
    ],
  },
}

type Props = { locale: string }

export default function TelcoPage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <Layout>
      <SolutionMeta
        title={c.metaTitle}
        description={c.metaDescription}
        path="/solutions/industries/telco"
        locale={locale}
        bilingual
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow={c.heroEyebrow}
          title={
            <>
              {c.heroTitle[0]}
              <br />
              {c.heroTitle[1]}
            </>
          }
          body={c.heroBody}
          primaryCta={{ label: c.primaryCta, href: '/try/now' }}
          secondaryCta={{ label: c.secondaryCta, href: '/contact' }}
          badges={c.badges}
        />
        <SolutionPillars
          eyebrow={c.pillarsEyebrow}
          title={<>{c.pillarsTitle}</>}
          body={c.pillarsBody}
          pillars={c.pillars}
        />
        <ProductHowItWorks
          eyebrow={c.howEyebrow}
          title={<>{c.howTitle}</>}
          steps={c.steps}
        />
        <SolutionRelated
          eyebrow={c.relatedEyebrow}
          title={c.relatedTitle}
          items={c.related}
        />
        <FinalCta />
      </main>
    </Layout>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
